import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getArticleBySlug, getRelatedArticles, type Article } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';

const detailImages: Record<string, string> = {
  'jiaguwen-zhong-de-yin': 'https://readdy.ai/api/search-image?query=Oracle%20bone%20inscriptions%20of%20ancient%20Chinese%20characters%20on%20turtle%20shell%20fragments%2C%20archaeological%20museum%20photography%2C%20warm%20natural%20lighting%2C%20detailed%20texture%2C%20scholarly%20presentation&width=1200&height=600&seq=detail-01&orientation=landscape',
  'yin-zi-wuti-yanbian': 'https://readdy.ai/api/search-image?query=Evolution%20of%20Chinese%20calligraphy%20from%20seal%20script%20to%20cursive%2C%20five%20styles%20displayed%20horizontally%2C%20ink%20on%20rice%20paper%2C%20warm%20lighting%2C%20artistic%20scholarly%20presentation&width=1200&height=600&seq=detail-02&orientation=landscape',
  'cong-yin-dao-yin': 'https://readdy.ai/api/search-image?query=Chinese%20character%20radical%20components%20analysis%20with%20phonetic%20elements%20highlighted%2C%20educational%20diagram%20on%20aged%20paper%2C%20warm%20sepia%2C%20scholarly&width=1200&height=600&seq=detail-03&orientation=landscape',
  'yin-zi-ben-yi-kao': 'https://readdy.ai/api/search-image?query=Continuous%20rain%20over%20traditional%20Chinese%20courtyard%2C%20ink%20wash%20painting%20style%2C%20atmospheric%20mood%2C%20classical%20Chinese%20aesthetic%2C%20poetic%20rain%20scene&width=1200&height=600&seq=detail-04&orientation=landscape',
  'yin-zi-yuyi-wangluo': 'https://readdy.ai/api/search-image?query=Chinese%20semantic%20network%20mind%20map%20with%20characters%20connected%20by%20ink%20lines%2C%20aged%20paper%20background%2C%20warm%20lighting%2C%20linguistics%20research%20visualization&width=1200&height=600&seq=detail-05&orientation=landscape',
  'yin-yu-yin-yu-yao': 'https://readdy.ai/api/search-image?query=Comparison%20of%20three%20similar%20Chinese%20characters%20with%20water%20rain%20and%20woman%20radicals%2C%20traditional%20dictionary%20style%2C%20warm%20lighting%2C%20educational&width=1200&height=600&seq=detail-06&orientation=landscape',
  'shijing-zhong-de-yin': 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20Book%20of%20Songs%20poetry%20scroll%20unfurled%20on%20wooden%20desk%2C%20ink%20brush%20nearby%2C%20warm%20natural%20light%2C%20classical%20literary%20atmosphere&width=1200&height=600&seq=detail-07&orientation=landscape',
  'zuozhuan-xianqin-yin': 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20historical%20bamboo%20slips%20arranged%20on%20display%2C%20museum%20quality%20photography%2C%20warm%20lighting%2C%20traditional%20historiography%20artifacts&width=1200&height=600&seq=detail-08&orientation=landscape',
  'shuowenjiezi-xunshi-yin': 'https://readdy.ai/api/search-image?query=Shuowen%20Jiezi%20ancient%20Chinese%20dictionary%20open%20pages%20with%20character%20entries%2C%20red%20seal%20marks%2C%20warm%20lighting%2C%20traditional%20philology&width=1200&height=600&seq=detail-09&orientation=landscape',
  'rujia-lijiao-zhong-de-yin': 'https://readdy.ai/api/search-image?query=Confucian%20Analects%20classic%20text%20on%20aged%20paper%20with%20traditional%20binding%2C%20warm%20lighting%2C%20Chinese%20philosophy%2C%20scholarly%20ancient%20books&width=1200&height=600&seq=detail-10&orientation=landscape',
  'le-er-bu-yin-kongzi': 'https://readdy.ai/api/search-image?query=Confucius%20portrait%20in%20traditional%20Chinese%20ink%20wash%20painting%20style%2C%20serene%20expression%2C%20classical%20composition%2C%20warm%20sepia%20tones%2C%20scholarly%20atmosphere&width=1200&height=600&seq=detail-11&orientation=landscape',
  'yin-zi-xiandai-hanyu': 'https://readdy.ai/api/search-image?query=Modern%20Chinese%20dictionary%20alongside%20digital%20tablet%20displaying%20Chinese%20corpus%20data%2C%20blending%20traditional%20and%20contemporary%2C%20warm%20lighting&width=1200&height=600&seq=detail-12&orientation=landscape',
};

const relatedImages: Record<string, string> = {
  'jiaguwen-zhong-de-yin': 'https://readdy.ai/api/search-image?query=Oracle%20bone%20script%20closeup%20on%20animal%20bone%2C%20ancient%20Chinese%20writing%2C%20museum%20artifact%2C%20warm%20lighting%2C%20archaeological%20detail&width=800&height=500&seq=rel-01&orientation=landscape',
  'yin-zi-wuti-yanbian': 'https://readdy.ai/api/search-image?query=Chinese%20calligraphy%20brush%20writing%20on%20rice%20paper%2C%20ink%2C%20traditional%20art%2C%20warm%20lighting%2C%20hand%20holding%20brush&width=800&height=500&seq=rel-02&orientation=landscape',
  'cong-yin-dao-yin': 'https://readdy.ai/api/search-image?query=Chinese%20radical%20decomposition%20of%20characters%2C%20linguistic%20analysis%2C%20aged%20paper%2C%20warm%20sepia%2C%20educational%20diagram&width=800&height=500&seq=rel-03&orientation=landscape',
  'yin-zi-ben-yi-kao': 'https://readdy.ai/api/search-image?query=Rain%20falling%20on%20traditional%20Chinese%20rooftop%2C%20ink%20wash%20style%2C%20atmospheric%2C%20poetic%2C%20classical%20Chinese%20painting&width=800&height=500&seq=rel-04&orientation=landscape',
  'yin-zi-yuyi-wangluo': 'https://readdy.ai/api/search-image?query=Web%20of%20interconnected%20Chinese%20characters%20on%20paper%2C%20linguistic%20network%2C%20warm%20lighting%2C%20academic%20illustration&width=800&height=500&seq=rel-05&orientation=landscape',
  'yin-yu-yin-yu-yao': 'https://readdy.ai/api/search-image?query=Chinese%20character%20variants%20comparison%20chart%2C%20traditional%20calligraphy%2C%20educational%2C%20warm%20lighting&width=800&height=500&seq=rel-06&orientation=landscape',
  'shijing-zhong-de-yin': 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20poetry%20book%20open%20pages%2C%20warm%20lighting%2C%20traditional%20binding%2C%20classical%20literature&width=800&height=500&seq=rel-07&orientation=landscape',
  'zuozhuan-xianqin-yin': 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20bamboo%20slips%20with%20historical%20text%2C%20museum%20display%2C%20warm%20lighting%2C%20archaeological%20artifact&width=800&height=500&seq=rel-08&orientation=landscape',
  'shuowenjiezi-xunshi-yin': 'https://readdy.ai/api/search-image?query=Qing%20dynasty%20philology%20annotated%20books%2C%20red%20ink%20marks%2C%20traditional%20study%2C%20warm%20lighting&width=800&height=500&seq=rel-09&orientation=landscape',
  'rujia-lijiao-zhong-de-yin': 'https://readdy.ai/api/search-image?query=Confucian%20texts%20traditional%20Chinese%20binding%2C%20warm%20lighting%2C%20philosophy%20books%2C%20ancient%20library&width=800&height=500&seq=rel-10&orientation=landscape',
  'le-er-bu-yin-kongzi': 'https://readdy.ai/api/search-image?query=Chinese%20ink%20painting%20of%20scholar%20teaching%2C%20traditional%20style%2C%20warm%20sepia%2C%20classical%20education%20scene&width=800&height=500&seq=rel-11&orientation=landscape',
  'yin-zi-xiandai-hanyu': 'https://readdy.ai/api/search-image?query=Chinese%20language%20corpus%20research%2C%20dictionary%20and%20laptop%2C%20modern%20linguistics%2C%20warm%20lighting&width=800&height=500&seq=rel-12&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const related = article ? getRelatedArticles(article.slug, article.category, 3) : [];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-20">
          <div className="px-4 max-w-6xl mx-auto text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您访问的文章不存在。</p>
            <Link to="/" className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </>
    );
  }

  const cat = categoryMap[article.category];
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: cat?.name || '', href: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    image: detailImages[article.slug] || detailImages['jiaguwen-zhong-de-yin'],
    publisher: {
      '@type': 'Organization',
      name: '淫字文化溯源',
    },
    inLanguage: 'zh-CN',
    about: {
      '@type': 'Thing',
      name: '淫',
    },
  };

  const imgSrc = detailImages[article.slug] || detailImages['jiaguwen-zhong-de-yin'];

  return (
    <>
      <Navbar />
      <main>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />

        <article className="pt-20 pb-10 md:pb-14">
          <div className="px-4 md:px-6 max-w-4xl mx-auto">
            <div className="mb-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            {cat && (
              <Link
                to={`/category/${article.category}`}
                className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-xs font-medium mb-4 hover:bg-primary-100 transition-colors duration-200 cursor-pointer"
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            )}

            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
            </div>
          </div>

          <div className="px-0 max-w-4xl mx-auto mb-8">
            <div className="relative h-56 md:h-72 overflow-hidden">
              <img
                src={imgSrc}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="px-4 md:px-6 max-w-3xl mx-auto">
            <div
              className="prose-custom text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200/70">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {related.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="px-4 md:px-6 max-w-6xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {related.map((rel) => (
                  <RelatedArticleCard key={rel.slug} article={rel} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}

function RelatedArticleCard({ article }: { article: Article }) {
  const cat = categoryMap[article.category];
  const imgSrc = relatedImages[article.slug] || relatedImages['jiaguwen-zhong-de-yin'];
  return (
    <article className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden transition-all duration-300 hover:border-primary-200">
      <Link to={`/article/${article.slug}`} className="block cursor-pointer">
        <div className="relative h-40 overflow-hidden">
          <img
            src={imgSrc}
            alt={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          {cat && (
            <span className="absolute top-2 left-2 px-2 py-0.5 bg-background-50/90 backdrop-blur-sm rounded text-xs font-medium text-foreground-700">
              {cat.name}
            </span>
          )}
        </div>
        <div className="p-4">
          <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-700 transition-colors duration-200">
            {article.title}
          </h3>
          <p className="text-xs text-foreground-500 line-clamp-2">{article.excerpt}</p>
        </div>
      </Link>
    </article>
  );
}